import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Download, Plus, Check, X, Trash2, Pencil } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function AdminWallets() {
  const [wallets, setWallets] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [rewardForm, setRewardForm] = useState(null);
  const [campaignForm, setCampaignForm] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txType, setTxType] = useState("");

  const load = useCallback(() => {
    api.get("/admin/wallets", { params: search ? { search } : {} }).then((r) => setWallets(r.data)).catch(() => {});
    api.get("/admin/redemptions").then((r) => setRedemptions(r.data)).catch(() => {});
    api.get("/admin/rewards").then((r) => setRewards(r.data)).catch(() => {});
    api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    api.get("/admin/transactions", { params: txType ? { type: txType } : {} }).then((r) => setTransactions(r.data)).catch(() => {});
  }, [search, txType]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const adjust = async (userId, sign) => {
    const val = window.prompt(sign > 0 ? "Nombre de RITAS à ajouter :" : "Nombre de RITAS à retirer :");
    if (!val) return;
    const amount = Math.abs(parseInt(val, 10)) * sign;
    if (!amount) return;
    const description = window.prompt("Motif (optionnel) :") || "Ajustement manuel";
    try {
      await api.post(`/admin/wallets/${userId}/adjust`, { amount, description });
      toast.success("Solde mis à jour");
      load();
      if (detail?.user?.id === userId) openDetail(userId);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDetail = async (userId) => {
    try { const { data } = await api.get(`/admin/wallets/${userId}`); setDetail(data); } catch {}
  };

  const processRedemption = async (id, status) => {
    try {
      await api.put(`/admin/redemptions/${id}`, { status });
      toast.success(status === "validee" ? "Récompense validée" : "Échange refusé, RITAS remboursés");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveReward = async (e) => {
    e.preventDefault();
    const payload = { ...rewardForm, threshold: Number(rewardForm.threshold) };
    try {
      if (rewardForm.id) await api.put(`/admin/rewards/${rewardForm.id}`, payload);
      else await api.post("/admin/rewards", payload);
      toast.success("Récompense enregistrée"); setRewardForm(null); load();
    } catch { toast.error("Erreur"); }
  };

  const saveCampaign = async (e) => {
    e.preventDefault();
    const payload = { ...campaignForm, multiplier: Number(campaignForm.multiplier || 1), flat_bonus: Number(campaignForm.flat_bonus || 0), start_date: campaignForm.start_date || null, end_date: campaignForm.end_date || null };
    try {
      if (campaignForm.id) await api.put(`/admin/campaigns/${campaignForm.id}`, payload);
      else await api.post("/admin/campaigns", payload);
      toast.success("Campagne enregistrée"); setCampaignForm(null); load();
    } catch { toast.error("Erreur"); }
  };

  const exportCsv = () => {
    const rows = [["Prénom", "Nom", "Email", "Téléphone", "Solde RITAS"], ...wallets.map((w) => [w.first_name, w.last_name, w.email, w.phone || "", w.balance])];
    const csv = rows.map((r) => r.map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = "fidelite-ritas-italian-club.csv";
    a.click();
  };

  const pending = redemptions.filter((r) => r.status === "en_attente");

  return (
    <div data-testid="admin-wallets">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-elegant text-3xl font-bold">Wallets RITAS</h1>
        <button data-testid="wallets-export-btn" onClick={exportCsv} className="btn-outline-gold !py-2 text-sm"><Download size={15} /> Exporter la liste fidélité</button>
      </div>

      {pending.length > 0 && (
        <div className="mb-10">
          <h2 className="gold-label mb-4">Échanges à valider ({pending.length})</h2>
          <div className="space-y-3">
            {pending.map((r) => (
              <div key={r.id} className="ic-card !transform-none p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-redemption-${r.id}`}>
                <div>
                  <p className="font-medium">{r.reward_title} <span className="text-[#D4AF37] font-mono text-sm ml-2">{r.code}</span></p>
                  <p className="text-xs text-gray-400 mt-1">{r.user_name} · {r.user_email} · {r.cost} RITAS</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`redemption-validate-${r.id}`} onClick={() => processRedemption(r.id, "validee")} className="p-2 rounded-lg bg-[#009246]/20 text-[#009246]" title="Valider"><Check size={16} /></button>
                  <button data-testid={`redemption-refuse-${r.id}`} onClick={() => processRedemption(r.id, "refusee")} className="p-2 rounded-lg bg-[#C8102E]/20 text-[#ff6b81]" title="Refuser"><X size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input data-testid="wallets-search" className="ic-input !pl-11" placeholder="Rechercher un client…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="ic-card !transform-none overflow-x-auto mb-10">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-left">
            <th className="p-4 gold-label">Client</th><th className="p-4 gold-label">Email</th><th className="p-4 gold-label">Solde RITAS</th><th className="p-4 gold-label">Actions</th>
          </tr></thead>
          <tbody>
            {wallets.map((w) => (
              <tr key={w.id} className="border-b border-white/5" data-testid={`wallet-row-${w.id}`}>
                <td className="p-4 font-medium">{w.first_name} {w.last_name}</td>
                <td className="p-4 text-gray-400">{w.email}</td>
                <td className="p-4 text-[#D4AF37] font-medium">{w.balance}</td>
                <td className="p-4 flex gap-2">
                  <button data-testid={`wallet-add-${w.id}`} onClick={() => adjust(w.id, 1)} className="text-xs border border-[#009246]/40 text-[#009246] rounded-full px-3 py-1">+ RITAS</button>
                  <button data-testid={`wallet-remove-${w.id}`} onClick={() => adjust(w.id, -1)} className="text-xs border border-[#C8102E]/40 text-[#ff6b81] rounded-full px-3 py-1">− RITAS</button>
                  <button data-testid={`wallet-history-${w.id}`} onClick={() => openDetail(w.id)} className="text-xs border border-white/15 text-gray-300 rounded-full px-3 py-1">Historique</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {wallets.length === 0 && <p className="p-6 text-gray-500">Aucun client.</p>}
      </div>

      {detail && (
        <div className="ic-card !transform-none p-6 mb-10" data-testid="wallet-detail">
          <div className="flex items-center justify-between mb-4">
            <p className="font-medium">Historique — {detail.user.first_name} {detail.user.last_name} · <span className="text-[#D4AF37]">{detail.balance} RITAS</span></p>
            <button onClick={() => setDetail(null)} className="text-gray-500 hover:text-white"><X size={16} /></button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {detail.transactions.map((t) => (
              <div key={t.id} className="flex justify-between text-sm border-b border-white/5 pb-2">
                <span className="text-gray-300">{t.description} <span className="text-gray-600 text-xs ml-2">{new Date(t.created_at).toLocaleDateString("fr-FR")}</span></span>
                <span className={t.amount >= 0 ? "text-[#009246]" : "text-[#ff6b81]"}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
              </div>
            ))}
            {detail.transactions.length === 0 && <p className="text-gray-500 text-sm">Aucune transaction.</p>}
          </div>
        </div>
      )}

      <div className="mb-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="gold-label">Historique global des transactions ({transactions.length})</h2>
          <select data-testid="tx-type-filter" className="ic-input !w-auto !py-1.5 text-xs" value={txType} onChange={(e) => setTxType(e.target.value)}>
            <option value="">Tous les types</option>
            <option value="payment">Paiement</option>
            <option value="manual_add">Ajout manuel</option>
            <option value="manual_remove">Retrait manuel</option>
            <option value="redeem">Échange récompense</option>
            <option value="birthday">Anniversaire</option>
            <option value="welcome">Bienvenue</option>
            <option value="first_reservation">1ère réservation</option>
            <option value="refund">Remboursement</option>
          </select>
        </div>
        <div className="ic-card !transform-none overflow-x-auto max-h-80 overflow-y-auto" data-testid="tx-global-history">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0a0a0a]"><tr className="border-b border-white/10 text-left">
              <th className="p-3 gold-label">Date</th><th className="p-3 gold-label">Client</th><th className="p-3 gold-label">Description</th><th className="p-3 gold-label text-right">RITAS</th>
            </tr></thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id} className="border-b border-white/5">
                  <td className="p-3 text-gray-500 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-3 text-gray-300">{t.user_name}</td>
                  <td className="p-3 text-gray-400">{t.description}{t.admin_email ? <span className="text-gray-600 text-xs ml-1">({t.admin_email})</span> : null}</td>
                  <td className={`p-3 text-right font-medium ${t.amount >= 0 ? "text-[#009246]" : "text-[#ff6b81]"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {transactions.length === 0 && <p className="p-5 text-gray-500 text-sm">Aucune transaction.</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="gold-label">Récompenses</h2>
            <button data-testid="reward-add-btn" onClick={() => setRewardForm({ title: "", description: "", threshold: 100, active: true })} className="btn-outline-gold !py-1.5 !px-4 text-xs"><Plus size={13} /> Ajouter</button>
          </div>
          {rewardForm && (
            <form onSubmit={saveReward} className="ic-card !transform-none p-4 mb-4 space-y-3" data-testid="reward-form">
              <input data-testid="reward-form-title" required className="ic-input" placeholder="Titre *" value={rewardForm.title} onChange={(e) => setRewardForm({ ...rewardForm, title: e.target.value })} />
              <input data-testid="reward-form-desc" className="ic-input" placeholder="Description" value={rewardForm.description} onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })} />
              <input data-testid="reward-form-threshold" required type="number" min="1" className="ic-input" placeholder="Seuil RITAS *" value={rewardForm.threshold} onChange={(e) => setRewardForm({ ...rewardForm, threshold: e.target.value })} />
              <div className="flex gap-2">
                <button type="submit" data-testid="reward-form-save" className="btn-gold !py-1.5 !px-4 text-xs">Enregistrer</button>
                <button type="button" onClick={() => setRewardForm(null)} className="btn-outline-gold !py-1.5 !px-4 text-xs">Annuler</button>
              </div>
            </form>
          )}
          <div className="space-y-2">
            {rewards.map((r) => (
              <div key={r.id} className="ic-card !transform-none p-4 flex items-center justify-between text-sm" data-testid={`admin-reward-${r.id}`}>
                <div><p className="font-medium">{r.title} — <span className="text-[#D4AF37]">{r.threshold} RITAS</span></p><p className="text-xs text-gray-500">{r.description}</p></div>
                <div className="flex gap-1">
                  <button onClick={() => setRewardForm({ ...r })} className="p-1.5 text-gray-400 hover:text-[#D4AF37]"><Pencil size={14} /></button>
                  <button data-testid={`reward-delete-${r.id}`} onClick={async () => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/rewards/${r.id}`); load(); } }} className="p-1.5 text-gray-400 hover:text-[#ff6b81]"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="gold-label">Campagnes bonus</h2>
            <button data-testid="campaign-add-btn" onClick={() => setCampaignForm({ title: "", multiplier: 2, flat_bonus: 0, start_date: "", end_date: "", active: true })} className="btn-outline-gold !py-1.5 !px-4 text-xs"><Plus size={13} /> Ajouter</button>
          </div>
          {campaignForm && (
            <form onSubmit={saveCampaign} className="ic-card !transform-none p-4 mb-4 space-y-3" data-testid="campaign-form">
              <input data-testid="campaign-form-title" required className="ic-input" placeholder="Titre (ex: Double RITAS ce week-end) *" value={campaignForm.title} onChange={(e) => setCampaignForm({ ...campaignForm, title: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-500">Multiplicateur (2 = double)</label>
                <input data-testid="campaign-form-multiplier" type="number" step="0.1" min="1" className="ic-input" value={campaignForm.multiplier} onChange={(e) => setCampaignForm({ ...campaignForm, multiplier: e.target.value })} /></div>
                <div><label className="text-xs text-gray-500">Bonus fixe (+RITAS)</label>
                <input data-testid="campaign-form-flat" type="number" min="0" className="ic-input" value={campaignForm.flat_bonus} onChange={(e) => setCampaignForm({ ...campaignForm, flat_bonus: e.target.value })} /></div>
                <input data-testid="campaign-form-start" type="date" className="ic-input" value={campaignForm.start_date || ""} onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })} />
                <input data-testid="campaign-form-end" type="date" className="ic-input" value={campaignForm.end_date || ""} onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input type="checkbox" checked={campaignForm.active} onChange={(e) => setCampaignForm({ ...campaignForm, active: e.target.checked })} className="accent-[#D4AF37]" /> Active
              </label>
              <div className="flex gap-2">
                <button type="submit" data-testid="campaign-form-save" className="btn-gold !py-1.5 !px-4 text-xs">Enregistrer</button>
                <button type="button" onClick={() => setCampaignForm(null)} className="btn-outline-gold !py-1.5 !px-4 text-xs">Annuler</button>
              </div>
            </form>
          )}
          <div className="space-y-2">
            {campaigns.map((c) => (
              <div key={c.id} className="ic-card !transform-none p-4 flex items-center justify-between text-sm" data-testid={`admin-campaign-${c.id}`}>
                <div>
                  <p className="font-medium">{c.title} {c.active ? <span className="text-[#009246] text-xs ml-1">(active)</span> : <span className="text-gray-500 text-xs ml-1">(inactive)</span>}</p>
                  <p className="text-xs text-gray-500">×{c.multiplier}{c.flat_bonus > 0 ? ` · +${c.flat_bonus} RITAS` : ""}{c.start_date ? ` · ${c.start_date} → ${c.end_date || "∞"}` : ""}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setCampaignForm({ ...c })} className="p-1.5 text-gray-400 hover:text-[#D4AF37]"><Pencil size={14} /></button>
                  <button onClick={async () => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/campaigns/${c.id}`); load(); } }} className="p-1.5 text-gray-400 hover:text-[#ff6b81]"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
            {campaigns.length === 0 && !campaignForm && <p className="text-gray-500 text-sm">Aucune campagne. Créez « Double RITAS ce week-end » par exemple !</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
